import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import {
  Button,
  Card,
  Flex,
  HeaderItalic,
  Sheet,
  Switch,
  Text,
  TextArea,
} from '@/components/common';
import { useAvailableToday } from '@/hooks';
import { NOTE_MAX_LENGTH } from '@/services';
import './notes.css';

interface NoteComposerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (text: string) => Promise<void>;
}

export function NoteComposerDialog({ open, onOpenChange, onSubmit }: NoteComposerDialogProps) {
  const { t } = useTranslation('notes');
  const { isLive, setLive } = useAvailableToday();
  const [text, setText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const share = async () => {
    const trimmed = text.trim();
    if (!trimmed || submitting) return;
    setSubmitting(true);
    try {
      await onSubmit(trimmed);
      setText('');
      onOpenChange(false);
    } catch {
      // Parent reports errors; keep the draft open.
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Sheet.Root open={open} onOpenChange={onOpenChange}>
      <Sheet.Content>
        <Sheet.Title className="sr-only">{t('composer.title')}</Sheet.Title>
        <Flex direction="column" gap="4" px="4" pb="4" pt="1">
          <Flex align="baseline" justify="between">
            <HeaderItalic size="5">{t('composer.title')}</HeaderItalic>
            <Text size="1" color="gray" className="font-mono">
              {t('composer.counter', { count: text.trim().length, max: NOTE_MAX_LENGTH })}
            </Text>
          </Flex>
          <TextArea
            autoFocus
            rows={3}
            maxLength={NOTE_MAX_LENGTH}
            variant="classic"
            value={text}
            onChange={e => setText(e.target.value)}
            placeholder={t('circle.placeholder')}
            data-testid="note-composer-input"
          />
          <Card variant="surface">
            <Flex align="center" justify="between" gap="3">
              <Flex direction="column" gap="1">
                <Text weight="bold">{t('composer.live.title')}</Text>
                <Text size="1" color="gray">
                  {t('composer.live.description')}
                </Text>
              </Flex>
              <Switch
                checked={isLive}
                onCheckedChange={setLive}
                aria-label={t('composer.live.title')}
              />
            </Flex>
          </Card>
          <Button
            size="md"
            fullWidth
            isLoading={submitting}
            disabled={!text.trim()}
            onClick={() => void share()}
            data-testid="note-composer-share"
          >
            {t('composer.share')}
          </Button>
        </Flex>
      </Sheet.Content>
    </Sheet.Root>
  );
}
